/**
 * Utilidades compartidas del flujo de tickeadora.
 * Centraliza helpers para nombrado de archivos, persistencia de tickets en el
 * backend y descarga manual cuando se requiere. Es usado tanto por el agente
 * como por los componentes React.
 */

import Cookies from 'js-cookie';
import axios from 'axios';

function getApiBaseURL() {
  const envUrl = (process.env.REACT_APP_SERVER_URL || '').trim();
  if (envUrl) {
    return envUrl.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost') {
      return 'http://localhost:5000';
    }

    const protocol = window.location.protocol === 'http:' ? 'http' : 'https';
    return `${protocol}://${host}:5000`;
  }

  return 'http://localhost:5000';
}

/**
 * Genera el nombre de archivo para el ticket
 * @param {string|number} saleId - ID de la venta
 * @returns {string} Nombre del archivo
 */
export function generateTicketFilename(saleId) {
  try {
    const tenant = Cookies.get('tenant') || 'demo';
    const usuario = Cookies.get('usuario') || 'usuario';
    const now = new Date();
    
    const fecha = now.toISOString().slice(0, 10).replace(/-/g, '');
    const hora = now.toTimeString().slice(0, 8).replace(/:/g, '');
    
    return `${tenant}_${usuario}_${fecha}_${hora}_ticket_${saleId}.txt`;
  } catch (error) {
    console.warn('Error generando nombre de archivo:', error);
    return `ticket_${saleId}_${Date.now()}.txt`;
  }
}

/**
 * Guarda el ticket en el servidor
 * @param {string|number} idVenta - ID de la venta
 * @param {string} ticketContent - Contenido del ticket
 * @returns {Promise<Object|null>} Resultado del guardado
 */
export async function saveTicketToServer(idVenta, ticketContent) {
  try {
    const serverURL = getApiBaseURL();
    
    const response = await axios.post(`${serverURL}/transaccionAPI/guardarTicket`, {
      idVenta: idVenta,
      ticketContent: ticketContent
    });
    
    if (response.data.ok) {
      console.log(`✅ Ticket guardado en servidor: ${response.data.fileName}`);
      console.log(`📁 Ubicación: client/src/comprobantes/tickets/`);
      return response.data;
    } else {
      throw new Error(response.data.error || 'Error desconocido');
    }
  } catch (error) {
    console.error('❌ Error al guardar ticket en servidor:', error);
    return null;
  }
}

/**
 * Descarga el ticket como archivo (fallback)
 * @param {string} ticketContent - Contenido del ticket
 * @param {string} filename - Nombre del archivo
 * @returns {boolean} true si se descarga exitosamente
 */
export function saveTicketFile(ticketContent, filename) {
  try {
    const blob = new Blob([ticketContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    
    console.log(`✅ Ticket descargado: ${filename}`);
    console.log(`📁 Ubicación: Carpeta de Descargas del usuario`);
    return true;
  } catch (error) {
    console.error('❌ Error al descargar ticket:', error);
    return false;
  }
}

/**
 * Obtiene parámetros globales necesarios para el ticket
 * @param {Function} getParametrosGlobales - Función para obtener parámetros
 * @returns {Promise<Object>} Parámetros globales
 */
export async function obtenerParametrosTicket(getParametrosGlobales) {
  try {
    const parametros = await getParametrosGlobales([
      'afipsdk_razon_social',
      'afipsdk_domicilio', 
      'afipsdk_telefono',
      'afipsdk_cuit',
      'afipsdk_condicion_iva',
      'usaTickeadora'
    ]);
    
    console.log('🔍 DEBUG: Parámetros globales obtenidos:', parametros);
    
    return parametros;
  } catch (error) {
    console.error('❌ Error obteniendo parámetros globales:', error);
    return {};
  }
}
